import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchGdTopics, deleteGdTopic, updateGdTopicStatus, fetchGdCategories } from "./adminGdService";

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || "";
const DIFFICULTIES = ["BEGINNER", "INTERMEDIATE", "ADVANCED"];
const STATUS_COLORS = { DRAFT: "#9e9e9e", COMING_SOON: "#ff9800", PUBLISHED: "#4caf50" };
const toPublicImgSrc = (url) => {
  if (!url) return "";
  const pub = url.replace(/\/api\/admin\/media-images\//, "/api/public/media-images/");
  console.log("[GdTopicsPage] thumbUrl raw:", url, "→ resolved:", `${BASE_ROOT}${pub}`);
  return `${BASE_ROOT}${pub}`;
};

let debounceTimer;

const AdminGdTopicsPage = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  useEffect(() => {
    fetchGdCategories().then((r) => {
      if (r.status === 200) setCategories(Array.isArray(r.data) ? r.data : r.data?.content || []);
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, size: 10 };
    if (search) params.search = search;
    if (categoryId) params.categoryId = categoryId;
    if (difficulty) params.difficulty = difficulty;
    const res = await fetchGdTopics(params);
    if (res.status === 200 && res.data) {
      setTopics(res.data.content || []);
      setTotalPages(res.data.totalPages || 0);
      setTotalElements(res.data.totalElements || 0);
    } else {
      setTopics([]);
    }
    setLoading(false);
  }, [page, search, categoryId, difficulty]);

  useEffect(() => { load(); }, [load]);

  const handleSearchChange = (e) => {
    clearTimeout(debounceTimer);
    const val = e.target.value;
    debounceTimer = setTimeout(() => { setPage(0); setSearch(val); }, 400);
  };

  const handleFilterChange = (setter) => (e) => { setPage(0); setter(e.target.value); };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this topic?")) return;
    const res = await deleteGdTopic(id);
    if (res.status === 200 || res.status === 204) load();
    else alert(res.message || "Delete failed");
  };

  const handleStatusChange = async (id, status) => {
    const res = await updateGdTopicStatus(id, status);
    if (res.status === 200) load();
    else alert(res.message || "Status update failed");
  };

  const thumbUrl = (t) => t.thumbnailUrl || t.category?.coverImageUrl || null;

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>
          GD Topics{" "}
          {totalElements > 0 && (
            <span style={{ fontSize: 14, fontWeight: 400, color: "#666" }}>({totalElements})</span>
          )}
        </h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => navigate("/admin/gd/categories")}
            style={{ background: "#fff", color: "#019095", border: "1px solid #019095", borderRadius: 6, padding: "8px 18px", cursor: "pointer" }}
          >
            Manage Categories
          </button>
          <button
            onClick={() => navigate("/admin/gd/topics/new")}
            style={{ background: "#019095", color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", cursor: "pointer" }}
          >
            + New Topic
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        <input
          type="text"
          placeholder="Search topics..."
          onChange={handleSearchChange}
          style={{ flex: 1, minWidth: 180, padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }}
        />
        <select value={categoryId} onChange={handleFilterChange(setCategoryId)}
          style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }}>
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={difficulty} onChange={handleFilterChange(setDifficulty)}
          style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }}>
          <option value="">All Difficulties</option>
          {DIFFICULTIES.map((d) => <option key={d}>{d}</option>)}
        </select>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className="announcement-table">
          <thead>
            <tr>
              <th>Thumbnail</th>
              <th>Title</th>
              <th>Category</th>
              <th>Difficulty</th>
              <th>Status</th>
              <th>Sort</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {topics.length === 0 ? (
              <tr>
                <td colSpan="7">No topics found</td>
              </tr>
            ) : (
              topics.map((t) => (
                <tr key={t.id}>
                  <td>
                    {thumbUrl(t) ? (
                      <img
                        src={toPublicImgSrc(thumbUrl(t))}
                        alt={t.title}
                        style={{ width: 56, height: 40, objectFit: "cover", borderRadius: 6 }}
                      />
                    ) : (
                      <div style={{ width: 56, height: 40, background: "#f0f0f0", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                        🗣️
                      </div>
                    )}
                  </td>
                  <td style={{ fontWeight: 600 }}>{t.title}</td>
                  <td>
                    {t.category && (
                      <span style={{ background: t.category.color + "22", color: t.category.color, border: `1px solid ${t.category.color}44`, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                        {t.category.name}
                      </span>
                    )}
                  </td>
                  <td>
                    <span style={{ fontSize: 12, background: "#f0f0f0", borderRadius: 999, padding: "2px 10px" }}>
                      {t.difficulty}
                    </span>
                  </td>
                  <td>
                    <select
                      value={t.status}
                      onChange={(e) => handleStatusChange(t.id, e.target.value)}
                      style={{ fontSize: 12, padding: "3px 8px", borderRadius: 6, border: `1px solid ${STATUS_COLORS[t.status]}`, color: STATUS_COLORS[t.status], background: STATUS_COLORS[t.status] + "18", cursor: "pointer" }}
                    >
                      <option value="DRAFT">DRAFT</option>
                      <option value="COMING_SOON">COMING_SOON</option>
                      <option value="PUBLISHED">PUBLISHED</option>
                    </select>
                  </td>
                  <td style={{ textAlign: "center" }}>{t.sortOrder ?? "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button onClick={() => navigate(`/admin/gd/topics/${t.id}/edit`)}>Edit</button>
                    <button onClick={() => navigate(`/admin/gd/topics/${t.id}/comments`)}>Comments</button>
                    <button className="delete-btn" onClick={() => handleDelete(t.id)}>Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div style={{ marginTop: 20 }}>
          <button disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</button>
          <span style={{ margin: "0 10px" }}>Page {page + 1} / {totalPages}</span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
};

export default AdminGdTopicsPage;
